
import { itemMapReducer, IItemMapState, updateItems, getItem, pruneItems } from './itemMapReducer';
import { keyMapReducer, IKeyMapState, addKeys, remapKeys, getMappedKey } from './keyMapReducer';
import { operationMapReducer, IOperationMapState } from './operationMapReducer';
import { IFaceted } from '../../models/Facet';
import { graft as defaultGraft, ReplacePatch } from '../../utilities/Graft';
import { IAction, IActionCreator, createActionCreator } from '../actions/createActionCreator';

/**
 * Represents the state of the item cache.
 * Do not attempt to navigate this state directly.
 * Instead, use `getCacheItem` and `getCacheDemands`
 * to fetch information from the store, and dispatch
 * actions such as `updateItems` to write information.
 *
 * @public
 */
export interface IItemCacheState {
  readonly itemMap: IItemMapState;
  readonly keyMap: IKeyMapState;
  readonly operationMap: IOperationMapState;
}

/**
 * Primitive store action to process multiple actions in a single atomic update.
 *
 * @internal
 */
export const processBatch: IActionCreator<'processBatch', {
  actions: IAction[];
}> = createActionCreator('processBatch');

const INITIAL_ITEM_CACHE_STATE: IItemCacheState = {
  itemMap: itemMapReducer(undefined, undefined),
  keyMap: keyMapReducer(undefined, undefined),
  operationMap: operationMapReducer(undefined, undefined)
};

/**
 * Reduces the state of an item cache with an incoming action, producing a new state if necessary.
 * @internal
 */
export function itemCacheReducer(state: IItemCacheState = INITIAL_ITEM_CACHE_STATE, action?: IAction | undefined, options: {
  graft?: typeof defaultGraft
} = {}): IItemCacheState {
  const {
    graft = defaultGraft
  } = options;

  if (action) {
    if (processBatch.matches(action)) {
      for (const batchAction of action.payload.actions) {
        // Process a series of actions as a batch.
        state = itemCacheReducer(state, batchAction, options);
      }
    } else if (updateItems.matches(action)) {
      // Need to map all the input keys.

      const {
        items
      } = action.payload;

      const keyMapState = keyMapReducer(state.keyMap, addKeys({
        keys: Object.keys(items)
      }), options);

      return graft(state, {
        keyMap: new ReplacePatch(keyMapState),
        itemMap: new ReplacePatch(itemMapReducer(state.itemMap, updateItems({
          items: remapKeys(keyMapState, items)
        }), options))
      });
    } else if (pruneItems.matches(action)) {
      // Need to map all the input keys.

      const {
        items
      } = action.payload;

      return graft(state, {
        itemMap: new ReplacePatch(itemMapReducer(state.itemMap, pruneItems({
          items: remapKeys(state.keyMap, items)
        }), options))
      });
    } else {
      return graft(state, {
        keyMap: new ReplacePatch(keyMapReducer(state.keyMap, action, options)),
        itemMap: new ReplacePatch(itemMapReducer(state.itemMap, action, options)),
        operationMap: new ReplacePatch(operationMapReducer(state.operationMap, action, options))
      });
    }
  }

  return state;
}

/**
 * Gets the current snapshot of an item in the cache by its key.
 * @internal
 */
export function getCacheItem(state: IItemCacheState, key: string): IFaceted | undefined {
  return getItem(state.itemMap, getMappedKey(state.keyMap, key));
}
